// Shared plumbing of lane B2's requisition, assignment and Compensation screens: reads that hold the capture settle
// signal until their data arrives, the route's query flags, and the record's status words from the server's keys.
// The settled read and the route flags live in views/shared/settledRead.ts (shared with lane B3) and are re-exported here.

import { COMPENSATION_REVIEW_STATUS, RECRUITER_ASSIGNMENT_STATUS, REQUISITION_STATUS } from "@/mc";

export { queryOf, useSettledRead, withQuery } from "@/views/shared/settledRead";

/** The requisition reads and lists a write on the requisition changes. */
export const requisitionReads = (id: string) =>
  [["requisition", id], ["requisitions"], ["today"], ["intake", id], ["health", id], ["readiness", id], ["timeline", id]] as const;

/** "Request Approved -> Req Creation Pending -> Created -> Posted." — the creation track's milestone keys. */
export const CREATION_WORD: Readonly<Record<string, string>> = {
  req_creation_pending: REQUISITION_STATUS.reqCreationPending,
  created: REQUISITION_STATUS.created,
  posted: REQUISITION_STATUS.posted,
};

/** "Recommended -> Pending TA Manager -> Assigned." — `requisition.assignment_status`. */
export const ASSIGNMENT_WORD: Readonly<Record<string, string>> = {
  recommended: RECRUITER_ASSIGNMENT_STATUS.recommended,
  pending_ta_manager: RECRUITER_ASSIGNMENT_STATUS.pendingTaManager,
  assigned: RECRUITER_ASSIGNMENT_STATUS.assigned,
};

export type AssignmentKey = keyof typeof RECRUITER_ASSIGNMENT_STATUS;
export const ASSIGNMENT_KEY: Readonly<Record<string, AssignmentKey>> = {
  recommended: "recommended", pending_ta_manager: "pendingTaManager", assigned: "assigned",
};

/** "Compensation: Pending -> In Review -> Finalized." — `compensation_review.status`. */
export const COMP_WORD: Readonly<Record<string, string>> = {
  pending: COMPENSATION_REVIEW_STATUS.pending,
  in_review: COMPENSATION_REVIEW_STATUS.inReview,
  finalized: COMPENSATION_REVIEW_STATUS.finalized,
};

/** The administrative states with a word of his ("Additional states: On Hold, Cancellation Pending/Cancelled, …"). */
export const GOVERNANCE_WORD: Readonly<Record<string, string>> = {
  on_hold: REQUISITION_STATUS.onHold,
  closed_cancelled: REQUISITION_STATUS.cancelled,
  filled: REQUISITION_STATUS.filled,
  closed_filled: REQUISITION_STATUS.filled,
  archived: REQUISITION_STATUS.archived,
};

/** A money figure from the server's decimal string, as the record's currency shows it. */
export function money(value: string | null | undefined, currency: string | null | undefined): string {
  if (value === null || value === undefined || value === "") return "—";
  const n = Number(value);
  if (!Number.isFinite(n)) return value;
  try {
    return new Intl.NumberFormat("en-US", { style: currency ? "currency" : "decimal", currency: currency ?? undefined, maximumFractionDigits: 0 }).format(n);
  } catch {
    return `${n.toLocaleString("en-US")}${currency ? ` ${currency}` : ""}`;
  }
}

/** "min – max" for a range; "—" when neither end is known. */
export function range(min: string | null | undefined, max: string | null | undefined, currency: string | null | undefined): string {
  if ((min ?? "") === "" && (max ?? "") === "") return "—";
  return `${money(min, currency)} – ${money(max, currency)}`;
}
